
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ProfileContext from '../context/ProfileContext';
import {
    Layout, Menu, theme, Button, Drawer, Avatar, Popover, Spin, Typography, Tag,
    Divider, AutoComplete, Input, Space
} from 'antd';
import {
    DashboardOutlined,
    UserOutlined,
    CheckCircleOutlined,
    LogoutOutlined,
    BookOutlined,
    TeamOutlined,
    SettingOutlined,
    SolutionOutlined,
    FileTextOutlined,
    ReadOutlined,
    CalendarOutlined,
    MenuOutlined,
    HistoryOutlined,
    InboxOutlined,
    DashOutlined,
    SearchOutlined,
    HomeOutlined, // Added HomeOutlined for Dashboard bottom icon
    BarChartOutlined, // Added for Reports icon
    DollarOutlined,
    SwapOutlined,
    KeyOutlined,
    LockOutlined,
    SmileOutlined,
    HeartOutlined,
    SafetyOutlined,
    SkinOutlined,
    DeleteOutlined,
    CloseOutlined,
    CarOutlined,
    MessageOutlined,
    WhatsAppOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageLogger from '../hooks/usePageLogger';
import api, { imageBaseUrl } from '../services/api';
import { getAllClasses } from '../services/classService';
import { globalSearch } from '../services/searchService';
import { useNotification } from '../context/NotificationContext';
import { getTutorGroup } from '../services/assignmentService';
import { PageHeaderProvider } from '../context/PageHeaderContext';
import { useSettings } from '../context/SettingsContext';
import { useTemplate } from '../hooks/useTemplate';
// Import CSS to handle the bottom navigation bar styling
import './MainLayout.css';
import TrialBanner from './TrialBanner';
import QuickAccessBar from './QuickAccessBar';
import { useTranslation } from 'react-i18next';
import { InAppNotificationProvider } from '../context/InAppNotificationContext';
import NotificationPane from './NotificationPane';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

// Check interval in milliseconds (e.g., 5 minutes)
const ROLE_SYNC_INTERVAL = 5 * 60 * 1000;

// --- NEW COMPONENT: Mobile Bottom Navigation ---
const MobileBottomNav = React.memo(({ menuItems, location, navigate, onMenuClick, bottomSheetVisible, bottomNavConfig, onDrop }) => {
    const { t } = useTranslation();

    // Build a map of all leaf menu items
    const getAllLeafItems = useCallback((allMenuItems) => {
        const pathMap = {};

        const extractLeafItems = (items) => {
            items.forEach(item => {
                if (item.children) {
                    extractLeafItems(item.children);
                } else {
                    let label = item.key === '/' ? t('menu.home') : null;
                    if (!label && item.label && item.label.props) {
                        label = item.label.props.children;
                    }
                    if (label) {
                        pathMap[item.key] = { key: item.key, icon: item.icon, label };
                    }
                }
            });
        };

        extractLeafItems(allMenuItems);
        return pathMap;
    }, [t]);

    const pathMap = useMemo(() => getAllLeafItems(menuItems), [menuItems, getAllLeafItems]);

    // Get nav items based on configuration
    const navItems = useMemo(() => {
        // Use first 3 items from config, then Notifications, then Menu (managed outside)
        return bottomNavConfig.slice(0, 3).map(key => pathMap[key] || { key, icon: <HomeOutlined />, label: key }).filter(Boolean);
    }, [bottomNavConfig, pathMap]);

    // Function to get the current active key for highlighting
    const getActiveKey = () => {
        const currentPath = location.pathname;
        if (currentPath === '/') return '/';
        const activeItem = navItems.find(item =>
            item.key !== '/' && currentPath.startsWith(item.key)
        );
        return activeItem?.key || '/';
    };

    const activeKey = getActiveKey();

    // Handle drop on nav slot
    const handleDrop = (slotIndex, e) => {
        e.preventDefault();
        e.stopPropagation();
        const draggedKey = e.dataTransfer?.getData('navItemKey');
        if (draggedKey && onDrop) {
            onDrop(slotIndex, draggedKey);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <div className="mobile-bottom-nav">
            {navItems.map((item, index) => (
                <div
                    key={`${item.key}-${index}`}
                    className={`nav-item ${activeKey === item.key ? 'active' : ''} drop-zone`}
                    onClick={() => navigate(item.key)}
                    onDrop={(e) => handleDrop(index, e)}
                    onDragOver={handleDragOver}
                    data-slot-index={index}
                >
                    {item.icon}
                    <Text className="nav-label">{item.label}</Text>
                </div>
            ))}
            {/* Notifications Button - 4th item */}
            <div className="nav-item">
                <NotificationPane compact />
                <Text className="nav-label" style={{ marginTop: 2 }}>{t('menu.notifications') || 'Alerts'}</Text>
            </div>
            {/* Menu Button - 5th item */}
            <div
                className="nav-item menu-button"
                onClick={onMenuClick}
            >
                {bottomSheetVisible ? <CloseOutlined /> : <MenuOutlined />}
                <Text className="nav-label">{t('menu.menu')}</Text>
            </div>
        </div>
    );
});

// --- NEW COMPONENT: Draggable Bottom Sheet ---
const DraggableBottomSheet = ({ menuItems, bottomNavConfig, navigate, closeBottomSheet, token }) => {
    const { t } = useTranslation();
    const [isCustomizing, setIsCustomizing] = useState(false);
    const [pressTimer, setPressTimer] = useState(null);
    const [dragState, setDragState] = useState({
        isDragging: false,
        itemKey: null,
        item: null,
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
        overSlot: null
    });

    // Flatten all menu items into a single array
    const getAllItems = useCallback(() => {
        const items = [];
        menuItems.forEach(item => {
            if (!item.children) {
                items.push(item);
            } else {
                item.children.forEach(child => items.push(child));
            }
        });
        return items;
    }, [menuItems]);

    const allItems = useMemo(() => getAllItems(), [getAllItems]);

    // Filter out items that are already in the bottom nav
    const availableItems = useMemo(() => {
        return allItems.filter(item => !bottomNavConfig.includes(item.key));
    }, [allItems, bottomNavConfig]);

    // Get drop zone element at touch position
    const getDropZoneAtPosition = (x, y) => {
        const elements = document.elementsFromPoint(x, y);
        const dropZone = elements.find(el => el.hasAttribute('data-slot-index'));
        return dropZone ? parseInt(dropZone.getAttribute('data-slot-index')) : null;
    };

    // Handle touch start
    const handleTouchStart = (e, item) => {
        if (!isCustomizing) return;

        const touch = e.touches[0];

        const timer = setTimeout(() => {
            // Vibrate if available (haptic feedback)
            if (navigator.vibrate) {
                navigator.vibrate(50);
            }

            setDragState({
                isDragging: true,
                itemKey: item.key,
                item: item,
                startX: touch.clientX,
                startY: touch.clientY,
                currentX: touch.clientX,
                currentY: touch.clientY,
                overSlot: null
            });
        }, 500); // 500ms press-and-hold threshold

        setPressTimer(timer);
    };

    // Handle touch move
    const handleTouchMove = (e) => {
        if (dragState.isDragging) {
            e.preventDefault(); // Prevent scrolling while dragging
            const touch = e.touches[0];
            const overSlot = getDropZoneAtPosition(touch.clientX, touch.clientY);

            setDragState(prev => ({
                ...prev,
                currentX: touch.clientX,
                currentY: touch.clientY,
                overSlot: overSlot
            }));
        }
    };

    // Handle touch end
    const handleTouchEnd = (e, itemKey) => {
        if (pressTimer) {
            clearTimeout(pressTimer);
            setPressTimer(null);
        }

        if (dragState.isDragging) {
            // Get the drop zone we're over
            if (dragState.overSlot !== null) {
                // Perform drop
                const newConfig = [...bottomNavConfig];
                newConfig[dragState.overSlot] = dragState.itemKey;
                localStorage.setItem('bottomNavConfig', JSON.stringify(newConfig));

                // Haptic feedback for successful drop
                if (navigator.vibrate) {
                    navigator.vibrate([30, 10, 30]);
                }

                // Notify parent to update
                window.dispatchEvent(new CustomEvent('bottomNavConfigChanged', {
                    detail: newConfig
                }));
            }

            setDragState({
                isDragging: false,
                itemKey: null,
                item: null,
                startX: 0,
                startY: 0,
                currentX: 0,
                currentY: 0,
                overSlot: null
            });
        } else {
            // Quick tap - navigate only if not customizing or if it was just a tap
            if (!isCustomizing) {
                navigate(itemKey);
                closeBottomSheet();
            }
        }
    };

    // Handle touch cancel
    const handleTouchCancel = () => {
        if (pressTimer) {
            clearTimeout(pressTimer);
            setPressTimer(null);
        }

        setDragState({
            isDragging: false,
            itemKey: null,
            item: null,
            startX: 0,
            startY: 0,
            currentX: 0,
            currentY: 0,
            overSlot: null
        });
    };

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (pressTimer) {
                clearTimeout(pressTimer);
            }
        };
    }, [pressTimer]);

    const getRawLabel = (label) => {
        if (!label) return '';
        if (typeof label === 'object') {
            return label.props?.children || '';
        }
        return label;
    };

    return (
        <>
            <div className="bottom-sheet-handle" />
            <div style={{
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0 8px'
            }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                    {isCustomizing ? t('menu.dragToCustomize') || 'Drag icons to bottom bar to customize' : t('menu.tapToNavigate') || 'Tap to navigate'}
                </Text>
                <Button
                    size="small"
                    type={isCustomizing ? "primary" : "default"}
                    onClick={() => setIsCustomizing(!isCustomizing)}
                    style={{ borderRadius: '16px' }}
                >
                    {isCustomizing ? t('common.done') : t('common.customize')}
                </Button>
            </div>

            {!isCustomizing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', paddingBottom: '30px' }}>
                    {menuItems.map((group, gIdx) => {
                        if (group.type === 'divider') {
                            return <Divider key={gIdx} style={{ margin: '8px 0' }} />;
                        }

                        if (group.children && group.children.length > 0) {
                            return (
                                <div key={group.key || gIdx} style={{ padding: '0 8px' }}>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        fontWeight: 700, 
                                        textTransform: 'uppercase', 
                                        letterSpacing: '0.05em',
                                        color: '#8c8c8c',
                                        marginBottom: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <span style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: token.colorPrimary }}>{group.icon}</span>
                                        <span>{getRawLabel(group.label)}</span>
                                    </div>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                        gap: '12px'
                                    }}>
                                        {group.children.map((item) => (
                                            <div
                                                key={item.key}
                                                className="grid-item-interactive"
                                                onClick={() => {
                                                    navigate(item.key);
                                                    closeBottomSheet();
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '12px 6px',
                                                    borderRadius: '12px',
                                                    backgroundColor: '#f9f9f9',
                                                    border: '1px solid #f0f0f0',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <div style={{
                                                    fontSize: '22px',
                                                    color: token.colorPrimary
                                                }}>
                                                    {item.icon}
                                                </div>
                                                <Text style={{
                                                    fontSize: '11px',
                                                    textAlign: 'center',
                                                    fontWeight: 500,
                                                    color: '#262626',
                                                    lineHeight: 1.2,
                                                    maxWidth: '80px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical'
                                                }}>
                                                    {getRawLabel(item.label)}
                                                </Text>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={group.key || gIdx} style={{ padding: '0 8px' }}>
                                <div
                                    className="grid-item-interactive"
                                    onClick={() => {
                                        navigate(group.key);
                                        closeBottomSheet();
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        backgroundColor: '#f5f5f5',
                                        border: '1px solid #e8e8e8',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ fontSize: '20px', color: token.colorPrimary, display: 'flex', alignItems: 'center' }}>
                                        {group.icon}
                                    </div>
                                    <Text style={{ fontWeight: 600, color: '#262626' }}>
                                        {getRawLabel(group.label)}
                                    </Text>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '16px',
                        padding: '8px 0',
                        touchAction: 'none'
                    }}
                    onTouchMove={handleTouchMove}
                >
                    {availableItems.map((item) => {
                        const itemKey = item.key;
                        const isBeingDragged = dragState.isDragging && dragState.itemKey === itemKey;

                        return (
                            <div
                                key={itemKey}
                                onTouchStart={(e) => handleTouchStart(e, item)}
                                onTouchEnd={(e) => handleTouchEnd(e, itemKey)}
                                onTouchCancel={handleTouchCancel}
                                className="grid-item-interactive"
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '12px 8px',
                                    borderRadius: '12px',
                                    backgroundColor: `${token.colorPrimary}10`,
                                    border: `1px dashed ${token.colorPrimary}40`,
                                    opacity: isBeingDragged ? 0.3 : 1,
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                    touchAction: 'none'
                                }}
                            >
                                <div style={{
                                    width: '54px',
                                    height: '54px',
                                    borderRadius: '50%',
                                    backgroundColor: token.colorPrimary,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '22px',
                                    color: 'white',
                                    boxShadow: `0 2px 8px ${token.colorPrimary}40`,
                                    pointerEvents: 'none'
                                }}>
                                    {item.icon}
                                </div>
                                <Text style={{
                                    fontSize: '11px',
                                    textAlign: 'center',
                                    fontWeight: 500,
                                    color: token.colorText,
                                    lineHeight: 1.2,
                                    maxWidth: '80px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    pointerEvents: 'none'
                                }}>
                                    {getRawLabel(item.label)}
                                </Text>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Floating drag preview */}
            {dragState.isDragging && dragState.item && (
                <div
                    style={{
                        position: 'fixed',
                        left: dragState.currentX - 40,
                        top: dragState.currentY - 40,
                        width: '80px',
                        pointerEvents: 'none',
                        zIndex: 10000,
                        transition: 'none'
                    }}
                >
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        transform: 'scale(1.2)',
                        filter: 'drop-shadow(0 8px 24px rgba(0, 0, 0, 0.3))'
                    }}>
                        <div style={{
                            width: '54px',
                            height: '54px',
                            borderRadius: '50%',
                            backgroundColor: token.colorPrimary,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '22px',
                            color: 'white',
                            border: `3px solid ${token.colorPrimaryBorder}`
                        }}>
                            {dragState.item.icon}
                        </div>
                        <Text style={{
                            fontSize: '10px',
                            textAlign: 'center',
                            fontWeight: 600,
                            color: token.colorText,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            maxWidth: '70px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {getRawLabel(dragState.item.label)}
                        </Text>
                    </div>
                </div>
            )}
        </>
    );
};


const MainLayout = ({ children, customMenuGenerator }) => {
    const { token } = theme.useToken();
    const [collapsed, setCollapsed] = useState(false);
    const [siderWidth, setSiderWidth] = useState(250); // Default width
    const [isResizing, setIsResizing] = useState(false);
    const { t, i18n } = useTranslation();
    const { studentsLabel, classesLabel, marksLabel, examsLabel } = useTemplate();
    const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [profileData, setProfileData] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const [teacherClass, setTeacherClass] = useState(null);
    const [userTutorGroup, setUserTutorGroup] = useState([]);
    const [mobileSearchVisible, setMobileSearchVisible] = useState(false);
    const [bottomSheetVisible, setBottomSheetVisible] = useState(false);

    // Bottom Nav Configuration (customizable)
    const [bottomNavConfig, setBottomNavConfig] = useState(() => {
        const saved = localStorage.getItem('bottomNavConfig');
        return saved ? JSON.parse(saved) : ['/', '/students', '/attendance', '/marks'];
    });

    const direction = i18n.dir(i18n.language);
    const isRTL = direction === 'rtl';

    // Resizing Logic
    const startResizing = useCallback((mouseDownEvent) => {
        setIsResizing(true);
    }, []);

    const stopResizing = useCallback(() => {
        setIsResizing(false);
    }, []);

    const resize = useCallback((mouseMoveEvent) => {
        if (isResizing) {
            let newWidth = mouseMoveEvent.clientX;
            // Adjust for RTL if needed, but clientX is usually absolute. 
            // For RTL sidebar is on right, so math is inverted: window.innerWidth - clientX
            if (isRTL) {
                newWidth = window.innerWidth - mouseMoveEvent.clientX;
            }

            if (newWidth < 200) newWidth = 200;
            if (newWidth > 450) newWidth = 450;

            setSiderWidth(newWidth);
        }
    }, [isResizing, isRTL]);

    useEffect(() => {
        if (isResizing) {
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'col-resize';
        } else {
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        }

        window.addEventListener("mousemove", resize);
        window.addEventListener("mouseup", stopResizing);
        return () => {
            window.removeEventListener("mousemove", resize);
            window.removeEventListener("mouseup", stopResizing);
            // Cleanup in case component unmounts while resizing
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };
    }, [resize, stopResizing, isResizing]);


    // Search State
    const [searchOptions, setSearchOptions] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchHistory, setSearchHistory] = useState(() => {
        const saved = localStorage.getItem('searchHistory');
        return saved ? JSON.parse(saved) : [];
    });

    // Save search history
    const addToSearchHistory = useCallback((term) => {
        if (!term || term.length < 3) return;
        setSearchHistory(prev => {
            const newHistory = [term, ...prev.filter(t => t !== term)].slice(0, 5);
            localStorage.setItem('searchHistory', JSON.stringify(newHistory));
            return newHistory;
        });
    }, []);

    const clearSearchHistory = useCallback(() => {
        setSearchHistory([]);
        localStorage.removeItem('searchHistory');
        setSearchOptions([]);
    }, []);

    usePageLogger();
    const { logout, user, syncRole } = useAuth();
    const { message: notify } = useNotification();
    const navigate = useNavigate();
    const location = useLocation();

    // Fetch school settings
    const { schoolName, schoolLogo: logoUrl, settings } = useSettings();

    // --- Profile Picture and URL Logic ---
    const profilePictureFullUrl = useMemo(() => {
        if (!profileData?.profile_picture_url) return null;
        const cacheBuster = Date.now();
        return `${imageBaseUrl}${profileData.profile_picture_url}?cb=${cacheBuster}`;
    }, [profileData]);

    const refreshProfileData = useCallback(async () => {
        if (!user) return;
        setProfileLoading(true);
        try {
            const response = await api.get('/profile/me');
            setProfileData(response.data);
        } catch (error) {
            console.error('Failed to fetch user profile for layout header:', error);
        } finally {
            setProfileLoading(false);
        }
    }, [user]);

    useEffect(() => {
        refreshProfileData();
        if (user && user.role === 'teacher') {
            getTutorGroup(user.id)
                .then(studentIds => setUserTutorGroup(studentIds))
                .catch(err => {
                    console.error("Fetch Tutor Group Error:", err.message);
                    setUserTutorGroup([]);
                });
        }
    }, [user, refreshProfileData]);

    // Listen for bottom nav config changes from drag-and-drop
    useEffect(() => {
        const handleConfigChange = (event) => {
            setBottomNavConfig(event.detail);
        };

        window.addEventListener('bottomNavConfigChanged', handleConfigChange);
        return () => window.removeEventListener('bottomNavConfigChanged', handleConfigChange);
    }, []);

    // Fetch teacher's class if user is a teacher
    useEffect(() => {
        const fetchTeacherClass = async () => {
            if (user && user.role === 'teacher') {
                try {
                    const classes = await getAllClasses();
                    const myClass = classes.find(c => c.class_teacher_id === user.id);
                    setTeacherClass(myClass ? myClass.name : null);
                } catch (err) {
                    setTeacherClass(null);
                }
            } else {
                setTeacherClass(null);
            }
        };
        fetchTeacherClass();
    }, [user]);


    // Responsive handling: track mobile state and adjust drawer visibility
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) {
                setMobileDrawerVisible(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Role Synchronization Timer
    const executeSyncCheck = useCallback(async () => {
        const roleChanged = await syncRole();
        if (roleChanged) {
            notify.error('Your session has been terminated due to an administrator changing your permissions. Please log in again.', 10);
        }
    }, [syncRole, notify]);


    useEffect(() => {
        if (user) {
            executeSyncCheck();
            const intervalId = setInterval(executeSyncCheck, ROLE_SYNC_INTERVAL);
            return () => clearInterval(intervalId);
        }
    }, [user, executeSyncCheck]);


    // User / auth handlers
    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const toggleMobileDrawer = () => {
        setMobileDrawerVisible(!mobileDrawerVisible);
    };

    const closeMobileDrawer = useCallback(() => {
        setMobileDrawerVisible(false);
    }, []);

    const toggleBottomSheet = useCallback(() => {
        setBottomSheetVisible(!bottomSheetVisible);
    }, [bottomSheetVisible]);

    const closeBottomSheet = useCallback(() => {
        setBottomSheetVisible(false);
    }, []);

    // Handle drop on bottom nav slot
    const handleNavDrop = useCallback((slotIndex, draggedKey) => {
        const newConfig = [...bottomNavConfig];
        newConfig[slotIndex] = draggedKey;
        setBottomNavConfig(newConfig);
        localStorage.setItem('bottomNavConfig', JSON.stringify(newConfig));
    }, [bottomNavConfig]);

    // Global Search Handler
    const handleSearch = useCallback(async (value) => {
        if (!value) {
            // Show history if empty
            if (searchHistory.length > 0) {
                const historyOptions = [
                    {
                        label: (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#666' }}>
                                <span>Recent Searches</span>
                                <Button type="text" size="small" icon={<DeleteOutlined />} onClick={(e) => {
                                    e.stopPropagation();
                                    clearSearchHistory();
                                }}>Clear</Button>
                            </div>
                        ),
                        options: searchHistory.map(term => ({
                            value: term,
                            label: (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <HistoryOutlined style={{ color: '#999' }} />
                                    <span>{term}</span>
                                </div>
                            )
                        }))
                    }
                ];
                setSearchOptions(historyOptions);
            } else {
                setSearchOptions([]);
            }
            return;
        }

        if (value.length < 3) {
            setSearchOptions([]);
            return;
        }
        setSearchLoading(true);
        const results = await globalSearch(value);
        addToSearchHistory(value);

        const options = results.map(item => ({
            value: item.title,
            label: (
                <Link to={item.link} onClick={() => setSearchOptions([])}>
                    <Space>
                        {item.type === 'student' && <TeamOutlined style={{ color: '#00b96b' }} />}
                        {item.type === 'user' && <UserOutlined style={{ color: '#1890ff' }} />}
                        {item.type === 'class' && <SettingOutlined style={{ color: '#faad14' }} />}
                        <div>
                            <Text strong>{item.title}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>{item.description}</Text>
                        </div>
                    </Space>
                </Link>
            ),
        }));
        setSearchOptions(options);
        setSearchLoading(false);
    }, [searchHistory, addToSearchHistory, clearSearchHistory]);

    // Menu generation helper:
    const getMenuItems = useCallback((user, closeMobileDrawer) => {
        if (!user) return [];
        const isTutor = user.role === 'teacher' && userTutorGroup.includes(user.id);
        const isAdmin = user.role === 'super_admin' || user.role === 'developer';
        const modules = settings?.modules_enabled || {};

        const isEnabled = (moduleKey) => {
            if (!modules) return true; // Default to true if settings not loaded yet
            return modules[moduleKey] !== false;
        };

        // Helper function for creating a Menu item with the Link component
        const createMenuItem = (key, icon, label, moduleKey) => {
            return {
                key, icon, label: <Link to={key} onClick={closeMobileDrawer}>{label}</Link>
            };
        };

        // Helper function for creating a submenu
        const createSubMenu = (key, icon, label, children) => {
            // Filter children based on their implied module if possible, 
            // but usually we filter before calling this.
            // If no children remain, return null (to hide the submenu).
            if (!children || children.length === 0) return null;
            return { key, icon, label, children };
        };

        // 1. Dashboard (Common)
        const baseItems = [];
        if (isEnabled('dashboard')) {
            baseItems.push(createMenuItem('/', <DashboardOutlined />, t('common.dashboard'), 'dashboard'));
        }

        // 2. Student Management
        const studentItems = [];
        if (isEnabled('students')) studentItems.push(createMenuItem('/students', <TeamOutlined />, studentsLabel, 'students'));
        if (isEnabled('attendance')) studentItems.push(createMenuItem('/attendance', <CheckCircleOutlined />, t('common.attendance'), 'attendance'));
        if (isEnabled('marks')) studentItems.push(createMenuItem('/marks', <BookOutlined />, marksLabel, 'marks'));
        if (isEnabled('inquiries')) studentItems.push(createMenuItem('/inquiries', <InboxOutlined />, t('menu.admissionInquiries'), 'inquiries'));
        studentItems.push(createMenuItem('/alumni', <HistoryOutlined />, 'Alumni / Former Students', 'students'));


        // 2.2 Reception Management
        const receptionItems = [];
        receptionItems.push(createMenuItem('/reception', <UserOutlined />, 'Visitor Logs', 'reception'));
        const receptionSubmenu = createSubMenu('reception-mgmt', <UserOutlined />, 'Reception & Visitors', receptionItems);

        // 2.5 KG Management
        const kgItems = [];
        if (settings?.system_template === 'kg') {
            kgItems.push(createMenuItem('/kg/daily-logs', <SmileOutlined />, 'Daily Logs', 'students'));
            kgItems.push(createMenuItem('/kg/milestones', <HeartOutlined />, 'Milestones', 'students'));
            kgItems.push(createMenuItem('/kg/pickups', <SafetyOutlined />, 'Safety & Pickups', 'students'));
            kgItems.push(createMenuItem('/kg/reports', <BarChartOutlined />, 'Developmental Report', 'reports'));
        }
        const kgSubmenu = createSubMenu('kg-mgmt', <SmileOutlined />, 'KG Management', kgItems);

        // 3. Academic Management
        const academicItems = [];
        if (isEnabled('classes')) academicItems.push(createMenuItem('/classes', <TeamOutlined />, classesLabel, 'classes'));
        if (isEnabled('subjects')) academicItems.push(createMenuItem('/subjects', <ReadOutlined />, t('menu.subjects'), 'subjects'));
        if (isEnabled('timetable')) academicItems.push(createMenuItem('/timetable', <CalendarOutlined />, t('menu.timetable'), 'timetable'));

        if (isEnabled('assignments')) {
            const assignmentItems = [
                createMenuItem('/class-subjects', <BookOutlined />, 'Class Subjects', 'classes'),
                createMenuItem('/assignments/teacher-subjects', <UserOutlined />, 'Teacher Subjects', 'assignments'),
                createMenuItem('/assignments/class-assignments', <TeamOutlined />, 'Class Assignments', 'assignments'),
                createMenuItem('/assignments/tutor-groups', <SolutionOutlined />, 'Tutor Groups', 'assignments')
            ];
            academicItems.push(createSubMenu('assignment-mgmt', <SolutionOutlined />, t('menu.assignments'), assignmentItems));
        }

        if (isEnabled('exams')) academicItems.push(createMenuItem('/exams', <FileTextOutlined />, examsLabel, 'exams'));

        if (isEnabled('academic-year')) {
            const yearItems = [
                createMenuItem('/academic-years/history', <HistoryOutlined />, 'Year History', 'academic-year'),
                createMenuItem('/academic-years/promotion-paths', <TeamOutlined />, 'Promotion Paths', 'academic-year')
            ];
            academicItems.push(createSubMenu('academic-year-mgmt', <HistoryOutlined />, t('menu.academicYears'), yearItems));
        }


        // 4. HR & Users
        const hrItems = [];
        if (isEnabled('users')) {
            hrItems.push(createMenuItem('/users', <UserOutlined />, 'User Accounts', 'users'));
        }
        if (isEnabled('hr')) {
            hrItems.push(createMenuItem('/hr/staff', <TeamOutlined />, 'Staff Directory', 'hr'));
            hrItems.push(createMenuItem('/hr/leaves', <SolutionOutlined />, 'Leave Management', 'hr'));
        }


        // 5. Finance
        const financeItems = [];
        if (isEnabled('fees')) {
            const feeSubItems = [
                createMenuItem('/fees/heads', <SettingOutlined />, 'Fee Heads', 'fees'),
                createMenuItem('/fees/structure', <SolutionOutlined />, 'Fee Structure', 'fees'),
                createMenuItem('/fees/collection', <DollarOutlined />, 'Fee Collection', 'fees')
            ];
            financeItems.push(createSubMenu('fee-mgmt', <DollarOutlined />, t('menu.feeManagement'), feeSubItems));
        }


        // 6. Library (Existing)
        const libraryItems = [];
        if (isEnabled('library')) {
            libraryItems.push(createMenuItem('/library', <DashboardOutlined />, t('common.dashboard'), 'library'));
            libraryItems.push(createMenuItem('/library/books', <BookOutlined />, t('menu.books'), 'library'));
            libraryItems.push(createMenuItem('/library/issue', <SwapOutlined />, t('library.issueBook'), 'library'));
            libraryItems.push(createMenuItem('/library/return', <HistoryOutlined />, t('library.returnBook'), 'library'));
        }
        const librarySubmenu = createSubMenu('library', <ReadOutlined />, t('menu.library'), libraryItems);

        // 6.5 New Modules
        const hostelItems = [];
        if (isEnabled('hostel')) {
            hostelItems.push(createMenuItem('/hostel', <HomeOutlined />, 'Dashboard', 'hostel'));
        }

        const laundryItems = [];
        if (isEnabled('laundry')) {
            laundryItems.push(createMenuItem('/laundry', <SkinOutlined />, 'Dashboard', 'laundry'));
        }

        const hifzItems = [];
        if (isEnabled('hifz')) {
            hifzItems.push(createMenuItem('/hifz', <ReadOutlined />, 'Dashboard', 'hifz'));
        }

        const transportItems = [];
        // if (isEnabled('transport')) { // Enable check later
        transportItems.push(createMenuItem('/transport', <CarOutlined />, 'Transport', 'transport'));
        // }

        const communicationItems = [];
        communicationItems.push(createMenuItem('/communication', <MessageOutlined />, 'Communication', 'communication'));


        // 7. Reports & Logs
        const reportsItems = [];
        if (isEnabled('reports')) reportsItems.push(createMenuItem('/reports', <BarChartOutlined />, t('menu.reports'), 'reports'));
        if (isEnabled('logs')) reportsItems.push(createMenuItem('/logs', <DashOutlined />, t('menu.auditLogs'), 'logs'));


        // 8. System Configuration
        const systemConfigItems = [];
        if (isEnabled('settings')) systemConfigItems.push(createMenuItem('/settings', <SettingOutlined />, t('common.settings'), 'settings'));
        if (isEnabled('settings')) systemConfigItems.push(createMenuItem('/settings/license', <KeyOutlined />, t('menu.licenseManagement'), 'settings'));
        if (isEnabled('calendar')) systemConfigItems.push(createMenuItem('/calendar', <CalendarOutlined />, t('menu.schoolCalendar'), 'calendar'));

        if (isAdmin) {
            systemConfigItems.push(createMenuItem('/grades', <SolutionOutlined />, 'Grade Definitions', 'exams'));
            systemConfigItems.push(createMenuItem('/exam-types', <FileTextOutlined />, 'Exam Types', 'exams'));
            systemConfigItems.push(
                createMenuItem('/admin/system-control', <LockOutlined />, 'System Control', 'settings')
            );
            systemConfigItems.push(
                createMenuItem('/admin/whatsapp', <WhatsAppOutlined />, 'WhatsApp Setup', 'settings')
            );
        }


        // --- Role Based Assembly ---

        if (user.role === 'super_admin' || user.role === 'principal') {
            // Group 1: Core Dashboards
            if (isEnabled('dashboard')) {
                baseItems.push(createMenuItem('/', <DashboardOutlined />, t('common.dashboard'), 'dashboard'));
            }
            if (isEnabled('calendar')) {
                baseItems.push(createMenuItem('/calendar', <CalendarOutlined />, t('menu.schoolCalendar'), 'calendar'));
            }

            baseItems.push({ type: 'divider' });

            // Group 2: Student Sphere
            const studentItems = [];
            if (isEnabled('students')) studentItems.push(createMenuItem('/students', <UserOutlined />, studentsLabel, 'students'));
            if (isEnabled('attendance')) studentItems.push(createMenuItem('/attendance', <CheckCircleOutlined />, t('common.attendance'), 'attendance'));
            if (isEnabled('inquiries')) studentItems.push(createMenuItem('/inquiries', <InboxOutlined />, t('menu.admissionInquiries'), 'inquiries'));
            studentItems.push(createMenuItem('/alumni', <HistoryOutlined />, 'Alumni Directory', 'students'));

            const studentHub = createSubMenu('student-hub', <TeamOutlined />, 'Student Hub', studentItems);
            if (studentHub) baseItems.push(studentHub);

            if (kgSubmenu) baseItems.push(kgSubmenu);

            // Group 3: Academics & Assessment
            const academicSubItems = [];
            if (isEnabled('classes')) academicSubItems.push(createMenuItem('/classes', <TeamOutlined />, classesLabel, 'classes'));
            if (isEnabled('subjects')) academicSubItems.push(createMenuItem('/subjects', <ReadOutlined />, t('menu.subjects'), 'subjects'));
            if (isEnabled('timetable')) academicSubItems.push(createMenuItem('/timetable', <CalendarOutlined />, t('menu.timetable'), 'timetable'));

            if (isEnabled('assignments')) {
                academicSubItems.push(createSubMenu('assignment-sub', <SolutionOutlined />, t('menu.assignments'), [
                    createMenuItem('/class-subjects', <BookOutlined />, 'Class Subjects', 'classes'),
                    createMenuItem('/assignments/teacher-subjects', <UserOutlined />, 'Teacher Subjects', 'assignments'),
                    createMenuItem('/assignments/class-assignments', <TeamOutlined />, 'Class Assignments', 'assignments'),
                    createMenuItem('/assignments/tutor-groups', <SolutionOutlined />, 'Tutor Groups', 'assignments')
                ]));
            }
            if (isEnabled('academic-year')) {
                academicSubItems.push(createSubMenu('academic-year-mgmt', <HistoryOutlined />, t('menu.academicYears'), [
                    createMenuItem('/academic-years/history', <HistoryOutlined />, 'Year History', 'academic-year'),
                    createMenuItem('/academic-years/promotion-paths', <TeamOutlined />, 'Promotion Paths', 'academic-year')
                ]));
            }

            const academicGroup = createSubMenu('academic-mgmt', <BookOutlined />, 'Academics & Curriculum', academicSubItems);
            if (academicGroup) baseItems.push(academicGroup);

            const assessmentSubItems = [];
            if (isEnabled('exams')) {
                assessmentSubItems.push(createMenuItem('/exams', <FileTextOutlined />, examsLabel, 'exams'));
            }
            if (isAdmin) {
                assessmentSubItems.push(createMenuItem('/exam-types', <FileTextOutlined />, 'Exam Types', 'exams'));
                assessmentSubItems.push(createMenuItem('/grades', <SolutionOutlined />, 'Grade Definitions', 'exams'));
            }
            if (isEnabled('marks')) {
                assessmentSubItems.push(createMenuItem('/marks', <BookOutlined />, marksLabel, 'marks'));
            }

            const assessmentGroup = createSubMenu('assessment-mgmt', <FileTextOutlined />, 'Assessment & Marks', assessmentSubItems);
            if (assessmentGroup) baseItems.push(assessmentGroup);

            baseItems.push({ type: 'divider' });

            // Group 4: Administrative Operations
            const hrSubItems = [];
            if (isEnabled('users')) hrSubItems.push(createMenuItem('/users', <UserOutlined />, 'User Accounts', 'users'));
            if (isEnabled('hr')) {
                hrSubItems.push(createMenuItem('/hr/staff', <TeamOutlined />, 'Staff Directory', 'hr'));
                hrSubItems.push(createMenuItem('/hr/leaves', <CalendarOutlined />, 'Leave Management', 'hr'));
            }
            const hrGroup = createSubMenu('hr-mgmt', <SolutionOutlined />, 'Human Resources', hrSubItems);
            if (hrGroup) baseItems.push(hrGroup);

            const financeSubItems = [];
            if (isEnabled('fees')) {
                financeSubItems.push(createMenuItem('/fees/collection', <DollarOutlined />, 'Fee Collection', 'fees'));
                financeSubItems.push(createMenuItem('/fees/structure', <SolutionOutlined />, 'Fee Structure', 'fees'));
                financeSubItems.push(createMenuItem('/fees/heads', <SettingOutlined />, 'Fee Heads', 'fees'));
            }
            const financeGroup = createSubMenu('finance-mgmt', <DollarOutlined />, 'Finance & Fees', financeSubItems);
            if (financeGroup) baseItems.push(financeGroup);

            if (receptionSubmenu) baseItems.push(receptionSubmenu);

            baseItems.push({ type: 'divider' });

            // Group 5: Campus Facilities
            const facilityItems = [];
            if (isEnabled('library')) {
                facilityItems.push(createSubMenu('library-sub', <ReadOutlined />, t('menu.library'), [
                    createMenuItem('/library', <DashboardOutlined />, t('common.dashboard'), 'library'),
                    createMenuItem('/library/books', <BookOutlined />, t('menu.books'), 'library'),
                    createMenuItem('/library/issue', <SwapOutlined />, t('library.issueBook'), 'library'),
                    createMenuItem('/library/return', <HistoryOutlined />, t('library.returnBook'), 'library')
                ]));
            }
            if (isEnabled('hostel')) {
                facilityItems.push(createMenuItem('/hostel', <HomeOutlined />, 'Hostel Portal', 'hostel'));
            }
            if (isEnabled('laundry')) {
                facilityItems.push(createMenuItem('/laundry', <SkinOutlined />, 'Laundry Service', 'laundry'));
            }
            if (isEnabled('hifz')) {
                facilityItems.push(createMenuItem('/hifz', <ReadOutlined />, 'Hifz Program', 'hifz'));
            }
            facilityItems.push(createMenuItem('/transport', <CarOutlined />, 'Transport & Fleet', 'transport'));

            const facilitiesGroup = createSubMenu('facilities-mgmt', <HomeOutlined />, 'Campus Facilities', facilityItems);
            if (facilitiesGroup) baseItems.push(facilitiesGroup);

            // Group 6: Communications & WhatsApp
            const commSubItems = [];
            commSubItems.push(createMenuItem('/communication', <MessageOutlined />, 'Broadcast Messages', 'communication'));
            if (isAdmin) {
                commSubItems.push(createMenuItem('/admin/whatsapp', <WhatsAppOutlined />, 'WhatsApp Integration', 'settings'));
            }
            const commGroup = createSubMenu('communication-mgmt', <MessageOutlined />, 'Communication Hub', commSubItems);
            if (commGroup) baseItems.push(commGroup);

            baseItems.push({ type: 'divider' });

            // Group 7: Analytics & Governance
            const governanceSubItems = [];
            if (isEnabled('reports')) {
                governanceSubItems.push(createMenuItem('/reports', <BarChartOutlined />, t('menu.reports'), 'reports'));
            }
            if (isEnabled('logs')) {
                const showLogs = user?.role !== 'principal' || user?.username === 'developer';
                if (showLogs) {
                    governanceSubItems.push(createMenuItem('/logs', <DashOutlined />, t('menu.auditLogs'), 'logs'));
                }
            }
            const governanceGroup = createSubMenu('governance-mgmt', <BarChartOutlined />, 'Analytics & Reports', governanceSubItems);
            if (governanceGroup) baseItems.push(governanceGroup);

            // Group 8: System Administration
            const sysAdminSubItems = [];
            if (isEnabled('settings')) {
                sysAdminSubItems.push(createMenuItem('/settings', <SettingOutlined />, 'Global Settings', 'settings'));
                sysAdminSubItems.push(createMenuItem('/settings/license', <KeyOutlined />, 'License / Subscription', 'settings'));
            }
            if (isAdmin) {
                sysAdminSubItems.push(createMenuItem('/admin/system-control', <LockOutlined />, 'System Control', 'settings'));
            }
            const sysAdminGroup = createSubMenu('sys-admin-mgmt', <SettingOutlined />, 'System Administration', sysAdminSubItems);
            if (sysAdminGroup) baseItems.push(sysAdminGroup);

            return baseItems;
        }

        if (user.role === 'librarian') {
            if (librarySubmenu) baseItems.push(librarySubmenu);
            return baseItems;
        }

        // Teacher Menus
        if (isTutor) {
            if (isEnabled('students')) baseItems.push(createMenuItem('/students', <TeamOutlined />, t('menu.myStudents'), 'students'));
        }

        if (user.role === 'teacher') {
            const showStudentMenus = teacherClass || user.subject_teacher;
            if (showStudentMenus) {
                const teacherStudentItems = [];
                if (isEnabled('students')) teacherStudentItems.push(createMenuItem('/students', <TeamOutlined />, t('common.students'), 'students'));
                if (isEnabled('attendance')) teacherStudentItems.push(createMenuItem('/attendance', <CheckCircleOutlined />, t('common.attendance'), 'attendance'));
                if (isEnabled('marks')) teacherStudentItems.push(createMenuItem('/marks', <BookOutlined />, t('common.marks'), 'marks'));

                const teacherStudentSubmenu = createSubMenu('student-mgmt', <TeamOutlined />, t('menu.studentManagement'), teacherStudentItems);

                if (teacherStudentSubmenu) baseItems.push(teacherStudentSubmenu);
                if (kgSubmenu) baseItems.push(kgSubmenu);

                // Divider before Ops/Reports
                if (baseItems.length > 0) baseItems.push({ type: 'divider' });

                if (isEnabled('hr')) {
                    baseItems.push(createMenuItem('/hr/leaves', <SolutionOutlined />, 'My Leave', 'hr'));
                }

                if (isEnabled('calendar')) baseItems.push(createMenuItem('/calendar', <CalendarOutlined />, t('menu.schoolCalendar'), 'calendar'));
                if (isEnabled('reports')) baseItems.push(createMenuItem('/reports', <BarChartOutlined />, t('menu.reports'), 'reports'));

                if (librarySubmenu) {
                    baseItems.push({ type: 'divider' });
                    baseItems.push(librarySubmenu);
                }
            }
            return baseItems;
        }

        return baseItems;
    }, [userTutorGroup, teacherClass, t, settings, studentsLabel, classesLabel, marksLabel, examsLabel]); // removed teachersLabel

    // Memoize menu items
    const menuItems = useMemo(() => {
        if (customMenuGenerator) {
            return customMenuGenerator(closeMobileDrawer);
        }
        return getMenuItems(user, closeMobileDrawer);
    }, [user, closeMobileDrawer, getMenuItems, customMenuGenerator]);

    // Accordion behaviour: only one top-level submenu open at a time.
    const rootSubmenuKeys = useMemo(
        () => (menuItems || []).filter(item => item && item.children).map(item => item.key),
        [menuItems]
    );
    const [openKeys, setOpenKeys] = useState([]);
    const onOpenChange = useCallback((keys) => {
        const latestOpenKey = keys.find(key => !openKeys.includes(key));
        // Opening a top-level submenu collapses the others; nested submenus
        // (latest key isn't a root) keep their parent chain open.
        if (latestOpenKey && rootSubmenuKeys.includes(latestOpenKey)) {
            setOpenKeys([latestOpenKey]);
        } else {
            setOpenKeys(keys);
        }
    }, [openKeys, rootSubmenuKeys]);

    // Find the submenu-key chain that leads to a given route path.
    const findOpenKeyChain = useCallback((items, targetPath) => {
        for (const item of items || []) {
            if (!item || !item.children) continue;
            if (item.children.some(c => c && c.key === targetPath)) return [item.key];
            const childChain = findOpenKeyChain(item.children, targetPath);
            if (childChain.length) return [item.key, ...childChain];
        }
        return [];
    }, []);

    // Keep the active route's submenu open on mount/refresh and when navigating
    // (so a page refresh doesn't collapse the drawer to nothing).
    useEffect(() => {
        const chain = findOpenKeyChain(menuItems, location.pathname);
        if (chain.length) setOpenKeys(chain);
    }, [location.pathname, menuItems, findOpenKeyChain]);

    // Memoize the Menu component
    const menuComponent = useMemo(() => (
        <Menu
            theme="light"
            selectedKeys={[location.pathname]}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            mode="inline"
            items={menuItems}
            style={{ borderRight: 0, fontSize: 16 }}
        />
    ), [menuItems, location.pathname, openKeys, onOpenChange]);


    // Content for the Popover (User Info / Logout)
    const popoverContent = (
        <div style={{ padding: '16px' }}>
            <p style={{ margin: '0 0 8px 0' }}>{t('library.loggedInAs')} <Text strong>{profileData?.full_name || user?.username}</Text></p>
            <p style={{ margin: '0 0 8px 0' }}>{t('library.role')} <Tag color="blue">{user?.role.toUpperCase()}</Tag></p>
            <Divider style={{ margin: '8px 0' }} />
            <Button
                type="link"
                icon={<UserOutlined />}
                onClick={() => { closeMobileDrawer(); navigate('/profile'); }}
                style={{ paddingLeft: 0 }}
            >
                {t('common.profile')}
            </Button>
            <Button
                type="default"
                icon={<LogoutOutlined />}
                onClick={() => {
                    if (window.confirm(t('common.logout') + '?')) {
                        handleLogout();
                    }
                }}
                danger
                style={{ marginTop: 8 }}
            >
                {t('common.logout')}
            </Button>
        </div>
    );

    // Determine content margin based on mobile/desktop and bottom nav presence
    const contentPaddingBottom = isMobile ? 80 : 24;

    const layoutStyle = {
        transition: isResizing ? 'none' : 'all 0.2s',
    };

    const siderStyle = {
        overflow: 'auto',
        height: '100vh',
        position: 'sticky',
        insetInlineStart: 0,
        top: 0,
        scrollbarWidth: 'thin',
        scrollbarGutter: 'stable',
        zIndex: 101,
        display: 'flex',
        flexDirection: 'column'
    };


    const schoolLogo = logoUrl ? `${imageBaseUrl}${logoUrl}` : require('../assets/logo.png');

    // --- NEW: Dynamic Mobile Title Logic ---
    // const { title: pageTitle } = usePageHeader(); // removed unused pageTitle

    return (
        <InAppNotificationProvider user={user}>
            <ProfileContext.Provider value={{ profileData, profileLoading, refreshProfileData, setProfileData }}>
                <Layout hasSider style={{ minHeight: '100vh' }}>

                    {/* Desktop Sidebar */}
                    {!isMobile && (
                        <Sider
                            theme='light'
                            collapsible
                            collapsed={collapsed}
                            onCollapse={(value) => setCollapsed(value)}
                            // breakpoint='xxl'
                            width={siderWidth}
                            style={siderStyle}
                        >
                            {/* Resizer Handle */}
                            {!collapsed && (
                                <div
                                    className={`sider-resizer ${isResizing ? 'resizing' : ''}`}
                                    onMouseDown={startResizing}
                                    style={{ '--resizer-hover-color': token.colorPrimary }}
                                />
                            )}

                            {/* Logo and Menu... */}
                            <div className="logo" style={{ margin: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <img
                                    src={schoolLogo}
                                    alt="School Logo"
                                    style={{ height: collapsed ? '40px' : '64px', transition: 'height 0.2s', objectFit: 'contain' }}
                                />
                            </div>
                            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: '60px' }}>
                                {menuComponent}
                            </div>
                        </Sider>
                    )}

                    {/* Mobile Drawer (used for the secondary menu items) */}
                    <Drawer
                        title={t('menu.allMenus')}
                        placement={isRTL ? "right" : "left"}
                        onClose={closeMobileDrawer}
                        open={mobileDrawerVisible}
                        width={300}
                        aria-label="Main navigation"
                        styles={{ body: { padding: 0 } }}
                    >
                        <div style={{ height: '100%', overflowY: 'auto', paddingBottom: '40px' }}>
                            {menuComponent}
                        </div>
                    </Drawer>

                    {/* Main content area */}
                    <Layout style={layoutStyle}>
                        <TrialBanner />

                        {/* Header (Simplified on Mobile) */}
                        <Header style={{
                            padding: isMobile ? '0 12px' : '0 24px',
                            background: token.colorBgContainer,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'sticky',
                            top: 0,
                            zIndex: 100, // Increased z-index
                        }}>
                            {/* Title/Menu Toggle - Hide on mobile if search is active */}
                            {(!isMobile || !mobileSearchVisible) && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <h2 style={{
                                        margin: 0,
                                        fontSize: isMobile ? '16px' : '20px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {isMobile ? schoolName : schoolName.toUpperCase()}
                                    </h2>
                                </div>
                            )}

                            {/* Search Input - Conditional on Mobile */}
                            {(isMobile ? mobileSearchVisible : true) && (
                                <div style={{
                                    flex: 1, // Take remaining space
                                    maxWidth: isMobile ? '100%' : 400,
                                    margin: isMobile ? '0' : '0 20px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    <AutoComplete
                                        options={searchOptions}
                                        onSearch={handleSearch}
                                        onFocus={() => handleSearch('')}
                                        style={{ width: '100%' }}
                                        filterOption={false}
                                        notFoundContent={searchLoading ? <Spin size="small" /> : null}
                                        popupMatchSelectWidth={isMobile ? 250 : 300}
                                        defaultActiveFirstOption={false}
                                    >
                                        <Input
                                            placeholder={t('dashboard.searchPlaceholder')}
                                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                            allowClear
                                            autoFocus={isMobile}
                                            style={{
                                                borderRadius: 6,
                                                fontSize: isMobile ? 16 : 14, // Prevent zoom on mobile
                                            }}
                                        />
                                    </AutoComplete>
                                </div>
                            )}

                            {/* Right Actions */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {/* Mobile Search Toggle */}
                                {isMobile && !mobileSearchVisible && (
                                    <Button
                                        type="text"
                                        icon={<SearchOutlined style={{ fontSize: 20 }} />}
                                        onClick={() => setMobileSearchVisible(true)}
                                    />
                                )}

                                {/* Mobile Search Close */}
                                {isMobile && mobileSearchVisible && (
                                    <Button
                                        type="text"
                                        icon={<CloseOutlined style={{ fontSize: 18 }} />}
                                        onClick={() => setMobileSearchVisible(false)}
                                        style={{ marginLeft: 4 }}
                                    />
                                )}

                                {/* Profile/Others - Hide on mobile if search is visible */}
                                {(!isMobile || !mobileSearchVisible) && (
                                    <>
                                        {teacherClass && (
                                            <Tag color="green" style={{ fontSize: isMobile ? 12 : 14 }}>
                                                {teacherClass}
                                            </Tag>
                                        )}

                                        {!isMobile && <NotificationPane />}

                                        <Spin spinning={profileLoading} size="small">
                                            <Popover
                                                placement="bottomRight"
                                                content={popoverContent}
                                                trigger="click"
                                                styles={{ body: { padding: 0 } }}
                                            >
                                                <Avatar
                                                    icon={<UserOutlined />}
                                                    size={isMobile ? 36 : 40}
                                                    src={profilePictureFullUrl}
                                                    alt={profileData?.full_name || user?.username}
                                                    style={{ cursor: 'pointer', backgroundColor: '#87d068' }}
                                                />
                                            </Popover>
                                        </Spin>

                                        {/* Mobile menu button (for secondary menu items) */}
                                        {isMobile && (
                                            <Button
                                                type="text"
                                                icon={<MenuOutlined />}
                                                onClick={toggleMobileDrawer}
                                                style={{ fontSize: '18px' }}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </Header>

                        {/* Content (adjusted padding) */}
                        <Content style={{
                            margin: isMobile ? '12px 8px 0' : '24px 16px',
                            padding: isMobile ? '16px 12px' : '24px',
                            paddingBottom: contentPaddingBottom,
                            minHeight: 360,
                            background: token.colorBgContainer,
                            borderRadius: isMobile ? 12 : token.borderRadiusLG
                        }}>
                            {/* Quick Access Navigation Bar - hidden on mobile */}
                            <QuickAccessBar isMobile={isMobile} />
                            {children}
                        </Content>
                    </Layout>

                    {/* --- NEW: Fixed Bottom Navigation for Mobile --- */}
                    {isMobile && (
                        <>
                            <MobileBottomNav
                                menuItems={menuItems}
                                location={location}
                                navigate={navigate}
                                onMenuClick={toggleBottomSheet}
                                bottomSheetVisible={bottomSheetVisible}
                                bottomNavConfig={bottomNavConfig}
                                onDrop={handleNavDrop}
                            />

                            {/* Bottom Sheet Drawer for All Menu Items */}
                            <Drawer
                                title={t('menu.allMenus')}
                                placement="bottom"
                                open={bottomSheetVisible}
                                onClose={closeBottomSheet}
                                height="70vh"
                                mask={true}
                                maskClosable={true}
                                zIndex={999}
                                styles={{
                                    body: {
                                        padding: '20px 16px 80px 16px',
                                        overflowY: 'auto'
                                    }
                                }}
                            >
                                <DraggableBottomSheet
                                    menuItems={menuItems}
                                    bottomNavConfig={bottomNavConfig}
                                    navigate={navigate}
                                    closeBottomSheet={closeBottomSheet}
                                    token={token}
                                />
                            </Drawer>
                        </>
                    )}
                </Layout>
            </ProfileContext.Provider>
        </InAppNotificationProvider>
    );
};

const MainLayoutWrapper = (props) => (
    <PageHeaderProvider>
        <MainLayout {...props} />
    </PageHeaderProvider>
);

export default MainLayoutWrapper;